import { motion } from "framer-motion";
import { Play, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { Eyebrow, Reveal, stagger, fadeUp } from "./motion";
import { PROOF } from "../config/content";

function Stars() {
  return (
    <div className="flex gap-1" aria-label="5 star rating">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className="w-4 h-4 text-[#FACC15] fill-[#FACC15]" />
      ))}
    </div>
  );
}

function TestimonialSlider() {
  const [index, setIndex] = useState(0);
  const items = PROOF.testimonials;
  const prev = () => setIndex((index - 1 + items.length) % items.length);
  const next = () => setIndex((index + 1) % items.length);

  return (
    <div className="mt-10" data-testid="testimonial-slider">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading uppercase text-white text-2xl tracking-wide">Video Stories <span className="text-[#6B7A70] text-base normal-case font-body">(placeholders)</span></h3>
        <div className="flex gap-2">
          <button onClick={prev} aria-label="Previous testimonial" data-testid="testimonial-prev" className="w-9 h-9 rounded-full border border-[#22C55E]/30 flex items-center justify-center text-[#22C55E] hover:bg-[#22C55E]/10 transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={next} aria-label="Next testimonial" data-testid="testimonial-next" className="w-9 h-9 rounded-full border border-[#22C55E]/30 flex items-center justify-center text-[#22C55E] hover:bg-[#22C55E]/10 transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="overflow-hidden">
        <motion.div
          className="flex gap-4"
          animate={{ x: `-${index * (100 / Math.min(items.length, 1))}%` }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          style={{ width: `${items.length * 100}%` }}
        >
          {items.map((t, i) => (
            <div key={i} className="w-full shrink-0" style={{ width: `${100 / items.length}%` }}>
              <div className="card-hover rounded-2xl bg-[#0F1712] border border-[#22C55E]/[0.18] overflow-hidden" data-testid={`testimonial-card-${i}`}>
                <div className="relative aspect-video bg-gradient-to-br from-[#0B110D] to-[#0F1712] flex items-center justify-center">
                  <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 30% 40%, rgba(34,197,94,0.3), transparent 60%)" }} aria-hidden="true" />
                  <button className="relative w-16 h-16 rounded-full bg-[#22C55E] flex items-center justify-center shadow-[0_0_30px_-4px_rgba(34,197,94,0.6)] hover:scale-105 transition-transform" aria-label="Play testimonial video" data-testid={`testimonial-play-${i}`}>
                    <Play className="w-6 h-6 text-[#04100A] fill-[#04100A] ml-1" />
                  </button>
                  <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest text-[#FACC15] border border-[#FACC15]/30 rounded-full px-2.5 py-1">Placeholder</span>
                </div>
                <div className="p-5">
                  <Stars />
                  <p className="mt-3 text-sm text-[#A7B3AA] leading-relaxed">{t.line}</p>
                  <p className="mt-3 text-sm font-semibold text-white">{t.name} <span className="text-[#6B7A70] font-normal">· {t.city}</span></p>
                </div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}

export default function Proof() {
  return (
    <section id="proof" className="py-16 md:py-24 bg-[#0B110D]" data-testid="proof-section">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>{PROOF.eyebrow}</Eyebrow>
          <h2 className="font-heading uppercase text-white text-4xl sm:text-5xl leading-none tracking-wide" data-testid="proof-heading">
            {PROOF.titleA} <span className="text-[#22C55E]">{PROOF.titleHighlight}</span>
          </h2>
          <p className="mt-3 text-sm text-[#6B7A70]">{PROOF.note}</p>
        </Reveal>

        <Reveal><TestimonialSlider /></Reveal>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="mt-14"
          data-testid="workspace-gallery"
        >
          <Reveal><h3 className="font-heading uppercase text-white text-2xl tracking-wide mb-4">Inside The Live Workspace</h3></Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {PROOF.gallery.map((g, i) => (
              <motion.figure key={i} variants={fadeUp} className={`group relative rounded-xl overflow-hidden border border-[#22C55E]/[0.18] ${i === 0 ? "col-span-2 aspect-[16/10] lg:aspect-auto lg:row-span-2" : "aspect-[4/3]"}`} data-testid={`gallery-item-${i}`}>
                <img src={g.image} alt={g.caption} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent px-3 pt-8 pb-2.5 text-xs text-white">{g.caption}</figcaption>
              </motion.figure>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
